import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";
import { DeleteConfirm } from "./AdminServices";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

const TestimonialModal = ({ testimonial, onClose, onSaved }) => {
  const { toast, show } = useToast();
  const isEdit = !!testimonial?.id;

  const [form, setForm] = useState({
    client_name: testimonial?.client_name || "",
    position:    testimonial?.position    || "",
    feedback:    testimonial?.feedback    || "",
    rating:      testimonial?.rating      || 5,
    category:    testimonial?.category    || "Software Development",
    initials:    testimonial?.initials    || "",
    color:       testimonial?.color       || "#0a6b5a",
    featured:    testimonial?.featured    || false,
    image:       testimonial?.image       || "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  // Auto-generate initials from client name
  const handleNameChange = (e) => {
    const name = e.target.value;
    const initials = name.split(" ").map((w) => w[0]).join("").substring(0, 2).toUpperCase();
    setForm((p) => ({ ...p, client_name: name, initials: p.initials || initials }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.feedback) {
      show("Name and feedback are required", "error"); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await authFetch(`${ENDPOINTS.testimonials}/${testimonial.id}`, {
          method: "PATCH",
          body: JSON.stringify({ ...form, rating: Number(form.rating) }),
        });
      } else {
        await authFetch(ENDPOINTS.testimonials, {
          method: "POST",
          body: JSON.stringify({ ...form, rating: Number(form.rating) }),
        });
      }
      show(isEdit ? "Testimonial updated!" : "Testimonial added!");
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const COLORS = ["#0a6b5a","#074a3f","#c3b996","#1d4ed8","#7c3aed","#b45309","#0f766e","#be123c"];

  return (
    <>
      <div className="adm-modal-backdrop" onClick={onClose}>
        <div className="adm-modal" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
          <div className="adm-modal-header">
            <h3 className="adm-modal-title">{isEdit ? "Edit Testimonial" : "Add Testimonial"}</h3>
            <button className="adm-modal-close" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="adm-modal-body">
              <div className="adm-form">

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Client Name *</label>
                    <input name="client_name" className="adm-form-input" value={form.client_name}
                      onChange={handleNameChange} placeholder="Amina Okonkwo" required />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Position / Company</label>
                    <input name="position" className="adm-form-input" value={form.position}
                      onChange={handleChange} placeholder="CEO, FinTrack Africa" />
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Feedback *</label>
                  <textarea name="feedback" className="adm-form-textarea" value={form.feedback}
                    onChange={handleChange} placeholder="What did they say about Solviotech?" style={{ minHeight: 100 }} required />
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Category</label>
                    <select name="category" className="adm-form-select" value={form.category} onChange={handleChange}>
                      {["Software Development","Mobile Development","UI/UX Design","Consulting","AI Solutions","DevOps"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Rating (1–5)</label>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", paddingTop: "0.5rem" }}>
                      {[1,2,3,4,5].map((n) => (
                        <button key={n} type="button"
                          onClick={() => setForm((p) => ({ ...p, rating: n }))}
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontSize: "1.25rem", color: n <= form.rating ? "#eab308" : "rgba(255,255,255,0.15)",
                            transition: "color 0.2s",
                          }}>
                          ★
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Initials (shown if no image)</label>
                    <input name="initials" className="adm-form-input" value={form.initials}
                      onChange={handleChange} placeholder="AO" maxLength={2} />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Avatar Color</label>
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", paddingTop: "0.4rem" }}>
                      {COLORS.map((c) => (
                        <button key={c} type="button"
                          onClick={() => setForm((p) => ({ ...p, color: c }))}
                          style={{
                            width: 28, height: 28, borderRadius: 6, background: c, border: "none", cursor: "pointer",
                            outline: form.color === c ? "2px solid white" : "none",
                            outlineOffset: 2,
                          }} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Photo URL (optional)</label>
                  <input name="image" className="adm-form-input" value={form.image}
                    onChange={handleChange} placeholder="https://..." />
                </div>

                <div className="adm-form-field">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", color: "rgba(255,255,255,0.6)", fontSize: "0.875rem" }}>
                    <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} style={{ accentColor: "#0a6b5a" }} />
                    Feature on homepage
                  </label>
                </div>

              </div>
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="adm-save-btn" disabled={saving}>
                {saving ? "Saving..." : <><i className="fa-solid fa-check"></i> {isEdit ? "Update" : "Add"}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

const AdminTestimonials = () => {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(null);
  const { toast, show }     = useToast();

  const load = useCallback(async () => {
    try {
      const data = await authFetch(ENDPOINTS.testimonials);
      setItems(data);
    } catch (err) {
      show(err.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Testimonials</h1>
          <p className="adm-page-sub">{items.length} testimonials</p>
        </div>
        <button className="adm-add-btn" onClick={() => setModal({ type: "add" })}>
          <i className="fa-solid fa-plus"></i> Add Testimonial
        </button>
      </div>

      <div className="adm-table-wrap">
        {loading ? (
          <div className="adm-loading"><div className="adm-loading-spinner"></div></div>
        ) : items.length === 0 ? (
          <div className="adm-empty">No testimonials yet.</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr><th>Client</th><th>Category</th><th>Rating</th><th>Featured</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: t.color || "#0a6b5a",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "'Syne',sans-serif", fontWeight: 700, color: "white", fontSize: "0.8rem",
                      }}>
                        {t.initials || t.client_name?.[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{t.client_name}</div>
                        <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)" }}>{t.position}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="adm-badge adm-badge--green">{t.category || "—"}</span></td>
                  <td style={{ color: "#eab308", letterSpacing: "0.05em" }}>
                    {"★".repeat(t.rating || 5)}
                  </td>
                  <td>
                    {t.featured
                      ? <span className="adm-badge adm-badge--gold">⭐ Featured</span>
                      : <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.8rem" }}>—</span>}
                  </td>
                  <td>
                    <div className="adm-action-btns">
                      <button className="adm-edit-btn" onClick={() => setModal({ type: "edit", item: t })}>
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button className="adm-delete-btn" onClick={() => setModal({ type: "delete", item: t })}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal?.type === "add" && <TestimonialModal testimonial={null} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "edit" && <TestimonialModal testimonial={modal.item} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "delete" && (
        <DeleteConfirm item={{ ...modal.item, name: modal.item.client_name }} label="Testimonial"
          endpoint={ENDPOINTS.testimonials} onClose={() => setModal(null)}
          onDeleted={() => { load(); show("Testimonial deleted."); }} />
      )}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
